/**
 * ui module entry (owner: save-ui-builder). Hooks consumed by src/core/wiring.ts:
 *   - attachUi(UIScene): installs dialogueBox / interactPrompt / pauseMenu / letterPanel / touchControls
 *     (plus uiRoot, toast, slotPanel) on the scene; torn down on its shutdown.
 *   - ensureFonts(): resolves once public/assets/fonts/fonts.css faces are ready (Preload awaits it).
 *   - renderTitle(TitleScene, { entries: [{ label, run }], onReady }): title visuals + menu (+ 设置).
 *   - renderChapterEnd(scene, { onSave, onTitle, onRestart }): chapter-end card.
 *   - registerShowcase(): adds `?showcase=ui`.
 */
import type Phaser from 'phaser';
import type { IDialogueBox } from '@/core/types/contracts';
import { SceneKeys } from '@/core/scenes/keys';
import { ensureDevUiTextures } from './devTextures';
import { DialogueBox } from './DialogueBox';
import { InteractPrompt } from './InteractPrompt';
import { LetterPanel } from './LetterPanel';
import { PauseMenu } from './PauseMenu';
import { SlotPanel } from './SlotPanel';
import { SkipHint } from './SkipHint';
import { Toast } from './Toast';
import { TouchControls, shouldEnableTouch } from './TouchControls';
import { UiRoot } from './UiRoot';
import { ensureFonts } from './fonts';

export { ensureFonts, getFontStatus, injectFontFaces, FONT_BODY, FONT_DISPLAY, FONT_TITLE, FONT_SERIF, FONT_FACES } from './fonts';
export { renderTitle, type TitleOptions, type TitleHandle } from './title/renderTitle';
export { buildTitleItems, type TitleEntry } from './title/items';
export { promptLabel, KIND_LABELS } from './promptLabel';
export { renderChapterEnd, type ChapterEndOptions, type ChapterEndHandle } from './chapterEnd/renderChapterEnd';
export { DialogueBox, FAST_TEXT_MULTIPLIER, type DialogueLine, type HistoryEntry, type PortraitRef } from './DialogueBox';
export { EMOTIONS, EMOTION_ALIASES, emotionChain, frameNameFor, resolveEmotionFrame, type Emotion } from './emotions';
export { SkipHint, SKIP_HINT_MS, SKIP_HINT_TEXT } from './SkipHint';
export { InteractPrompt } from './InteractPrompt';
export { LetterPanel } from './LetterPanel';
export { PauseMenu } from './PauseMenu';
export { SlotPanel, type SlotMode } from './SlotPanel';
export { TouchControls, shouldEnableTouch } from './TouchControls';
export { Toast } from './Toast';
export { UiRoot, uiRootOf } from './UiRoot';
export { MenuList, type MenuItem } from './MenuList';
export { uiText, makeMeasurer, currentZoom } from './text';
export { ensureDevUiTextures, DEV_UI_TEXTURES } from './devTextures';
export { Typewriter, buildSchedule, totalDuration, CHAR_MS } from './typewriter';
export { wrapText } from './wrap';
export { Palette, Css, UiDepth, Layout, Slices, UiTex } from './theme';
export { registerShowcase } from './showcase';

export interface UiComponents {
  uiRoot: UiRoot;
  dialogueBox: DialogueBox;
  interactPrompt: InteractPrompt;
  pauseMenu: PauseMenu;
  letterPanel: LetterPanel;
  touchControls: TouchControls;
  toast: Toast;
  slotPanel: SlotPanel;
  skipHint: SkipHint;
}

type UiHost = Phaser.Scene & Partial<UiComponents>;

/**
 * Installs every UI component on the (integrator-owned) UIScene. Idempotent per scene lifetime;
 * everything is torn down on the scene's shutdown so listener counts do not grow across round trips.
 */
export function attachUi(scene: Phaser.Scene): UiComponents {
  const host = scene as UiHost;
  if (host.uiRoot && host.dialogueBox) return host as UiComponents;
  ensureDevUiTextures(scene);
  scene.input.keyboard?.addCapture('TAB');
  const uiRoot = new UiRoot(scene);
  const toast = new Toast(uiRoot);
  const dialogueBox = new DialogueBox(uiRoot);
  const letterPanel = new LetterPanel(uiRoot);
  const pauseMenu = new PauseMenu(uiRoot, toast);
  const slotPanel = pauseMenu.slots;
  const suppressPrompt = () => dialogueBox.visible || letterPanel.isOpen() || uiRoot.hasModal();
  const interactPrompt = new InteractPrompt(uiRoot, suppressPrompt);
  const touchControls = new TouchControls(uiRoot, () => dialogueBox.visible || letterPanel.isOpen());
  touchControls.setEnabled(shouldEnableTouch(scene));
  if (touchControls.isEnabled()) interactPrompt.setKeyGlyph('点');
  const skipHint = new SkipHint(uiRoot, () => touchControls.isEnabled());
  const components: UiComponents = { uiRoot, dialogueBox, interactPrompt, pauseMenu, letterPanel, touchControls, toast, slotPanel, skipHint };
  Object.assign(host, components);
  uiRoot.track(() => { toast.destroy(); for (const k of Object.keys(components) as (keyof UiComponents)[]) delete host[k]; });
  void ensureFonts();
  return components;
}

/** The components installed on UIScene, or null before attachUi ran. */
export function getUi(scene: Phaser.Scene): UiComponents | null {
  const ui = scene.scene.get(SceneKeys.UI) as UiHost | null;
  return ui?.uiRoot && ui.dialogueBox ? (ui as UiComponents) : null;
}

/** Returns the dialogue box living in UIScene (a logging stub before attachUi ran). */
export function getDialogueBox(scene: Phaser.Scene): IDialogueBox {
  const ui = scene.scene.get(SceneKeys.UI) as (Phaser.Scene & { dialogueBox?: IDialogueBox }) | null;
  if (ui?.dialogueBox) return ui.dialogueBox;
  return {
    visible: false,
    async show(line) { console.log(`[dialogue-stub] ${line.speaker ?? ''}: ${line.text}`); },
    async waitForAdvance() { /* resolves immediately */ },
    completeTyping() { /* noop */ },
    isTyping: () => false,
    async hide() { /* noop */ },
    async showChoices() { return 0; },
    pushHistory() { /* noop */ },
  };
}
